import React from 'react';
import { Shield, Cloud, GitBranch, Lock, Workflow, Server, CheckCircle } from 'lucide-react';

const solutions = [
  {
    title: 'Kubernetes Security Hardening',
    description: 'Production-grade cluster security with Pod Security Standards, Network Policies, and OPA Gatekeeper policies.',
    icon: Shield,
    color: '#10B981',
    metrics: ['99.99% Uptime', 'Zero CVEs', 'CIS Compliant'],
    features: ['Pod Security Policies', 'Network Segmentation', 'RBAC Hardening', 'Secret Management']
  },
  {
    title: 'Multi-Cloud Infrastructure',
    description: 'Architecting resilient, cost-optimized infrastructure across AWS, Azure, and GCP with Terraform.',
    icon: Cloud,
    color: '#FF9900',
    metrics: ['500+ Resources', 'Multi-Region', '40% Cost Savings'],
    features: ['AWS EKS/Azure AKS', 'Terraform IaC', 'Cost Optimization', 'DR Solutions']
  },
  {
    title: 'GitOps & CI/CD Automation',
    description: 'Implementing continuous delivery pipelines with ArgoCD, GitHub Actions, and Jenkins for rapid, secure deployments.',
    icon: GitBranch,
    color: '#326CE5',
    metrics: ['70% Faster Deploys', 'Zero Downtime', '100+ Pipelines'],
    features: ['ArgoCD GitOps', 'GitHub Actions', 'Jenkins Pipelines', 'Automated Testing']
  },
  {
    title: 'Zero-Trust Architecture',
    description: 'Building defense-in-depth security with identity-based access, microsegmentation, and continuous verification.',
    icon: Lock,
    color: '#8B5CF6',
    metrics: ['85% Less Vulnerabilities', 'SOC2 Ready', 'Zero Breaches'],
    features: ['Identity-Based Access', 'Microsegmentation', 'Vault Integration', 'Audit Logging']
  },
  {
    title: 'Observability & Monitoring',
    description: 'Full-stack visibility with Prometheus, Grafana, and ELK Stack for proactive incident management.',
    icon: Workflow,
    color: '#EC4899',
    metrics: ['Real-time Alerts', 'Custom Dashboards', '<5min MTTR'],
    features: ['Prometheus/Grafana', 'ELK Stack', 'Custom Metrics', 'SLO Tracking']
  },
  {
    title: 'Container Security',
    description: 'Securing the entire container lifecycle from image scanning to runtime protection with Trivy and Falco.',
    icon: Server,
    color: '#F59E0B',
    metrics: ['0 Critical CVEs', 'Runtime Protection', 'Supply Chain Security'],
    features: ['Image Scanning', 'Runtime Security', 'Policy Enforcement', 'Compliance Reporting']
  }
];

const SolutionCard = ({ solution, index }) => {
  const Icon = solution.icon;
  
  return (
    <div 
      className="group relative bg-white rounded-2xl p-6 shadow-md border border-gray-100 hover:shadow-xl hover:border-gray-200 transition-all duration-300 overflow-hidden"
      data-testid={`solution-card-${index}`}
    >
      {/* Gradient overlay on hover */}
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity"
        style={{ background: `linear-gradient(135deg, ${solution.color}, transparent)` }}
      />
      
      <div className="relative z-10">
        {/* Icon & Title */}
        <div className="flex items-start gap-4 mb-4">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
            style={{ backgroundColor: `${solution.color}15` }}
          >
            <Icon className="w-6 h-6" style={{ color: solution.color }} />
          </div>
          <div>
            <h3 className="font-bold text-gray-800 text-lg mb-1 group-hover:text-purple-700 transition-colors">
              {solution.title}
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed">
              {solution.description}
            </p>
          </div>
        </div>

        {/* Metrics */}
        <div className="flex flex-wrap gap-2 mb-4">
          {solution.metrics.map((metric, i) => (
            <span 
              key={i}
              className="px-2.5 py-1 text-xs font-semibold rounded-full"
              style={{ 
                backgroundColor: `${solution.color}10`,
                color: solution.color
              }}
            >
              {metric}
            </span>
          ))}
        </div>

        {/* Features */}
        <div className="grid grid-cols-2 gap-1.5">
          {solution.features.map((feature, i) => (
            <div key={i} className="flex items-center gap-1.5 text-xs text-gray-600">
              <CheckCircle className="w-3 h-3 text-emerald-500 flex-shrink-0" />
              <span>{feature}</span>
            </div>
          ))}
        </div>
        
        {/* Bottom accent */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{ background: `linear-gradient(90deg, ${solution.color}, transparent)` }}
        />
      </div>
    </div>
  );
};

const DevSecOpsSolutionsSection = () => {
  return (
    <section className="py-20 bg-gradient-to-b from-white via-purple-50/30 to-white relative overflow-hidden" data-testid="devsecops-solutions-section">
      {/* Background decorations */}
      <div className="absolute top-10 right-10 text-6xl opacity-[0.03]">🛡️</div>
      <div className="absolute bottom-10 left-10 text-6xl opacity-[0.03]">⚡</div>
      
      <div className="max-w-6xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-100 mb-4">
            <Shield className="w-4 h-4 text-purple-600" />
            <span className="font-bold text-purple-600 text-sm">Enterprise Solutions</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-black text-gray-800 mb-3">
            DevSecOps Solutions{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
              Delivered
            </span>
          </h2>
          <p className="text-gray-500 text-base max-w-2xl mx-auto">
            Production-proven solutions securing Kubernetes clusters and automating cloud infrastructure at scale
          </p>
        </div>

        {/* Solutions Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {solutions.map((solution, index) => (
            <SolutionCard key={index} solution={solution} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default DevSecOpsSolutionsSection;
